// IE Hub - Shared Header Component
// Provides navigation for both mobile and desktop views with route-aware active states

import { useState, useRef, useEffect, useMemo } from "react";
import { Box, IconButton, Typography, Avatar } from "../ui";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, useForm } from "../shared/state/store";
import { ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";
import { OnboardingService } from "../shared/api/services";
import ProfileDialog from "./ProfileDialog";
import ContactUsDialog from "./ContactUsDialog";

interface NavTab {
  key: string;
  label: string;
  path: string;
  disabled?: boolean;
}

interface HeaderProps {
  onHeightChange?: (height: number) => void;
}

const Header: React.FC<HeaderProps> = ({ onHeightChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, openBankingOnly } = useAuth();
  const { formData } = useForm();

  // UW customer lock: hide header chrome for UW journey users.
  // Read isUwCustomer from localStorage (same pattern as RequireAuth guard).
  const isUwCustomer = useMemo(() => {
    try {
      const raw = localStorage.getItem("customerData");
      if (!raw) return false;
      const data = JSON.parse(raw);
      return data?.isUwCustomer === true;
    } catch {
      return false;
    }
  }, [location.pathname]);

  // Header measurement
  const headerRef = useRef<HTMLDivElement | null>(null);

  // Profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(
    null
  );

  // Contact Us dialog state
  const [contactUsDialogOpen, setContactUsDialogOpen] = useState(false);

  // Get user name and email
  const personalDetails =
    (formData?.["onboarding-step-3"] as Record<string, unknown> | undefined) ||
    {};
  const formFirstName =
    typeof personalDetails.firstName === "string"
      ? personalDetails.firstName.trim()
      : "";
  const displayNameOnly =
    typeof user?.displayName === "string" ? user.displayName.trim() : "";
  const getStoredCustomerFirstName = (): string => {
    try {
      const raw = localStorage.getItem("customerData");
      if (!raw) return "";
      const obj = JSON.parse(raw) as Record<string, unknown> | null;
      const candidate =
        (obj?.debtor1FirstName as string | undefined) ||
        (obj?.firstName as string | undefined) ||
        "";
      return typeof candidate === "string" ? candidate.trim() : "";
    } catch {
      return "";
    }
  };
  const truncateWithEllipsis = (value: string, maxLength = 20): string => {
    if (!value) return value;
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };

  const userNameRaw =
    getStoredCustomerFirstName() || formFirstName || displayNameOnly || "User";
  const userName = truncateWithEllipsis(userNameRaw, 20);

  // Get user email from stored data or form
  const getUserEmail = (): string | undefined => {
    try {
      const raw = localStorage.getItem("customerData");
      if (!raw) return undefined;
      const obj = JSON.parse(raw) as Record<string, unknown> | null;
      const email = obj?.email as string | undefined;
      return typeof email === "string" ? email : undefined;
    } catch {
      return undefined;
    }
  };
  const userEmail = getUserEmail();

  // Navigation tabs
  const headerNavTabs: NavTab[] = openBankingOnly
    ? []
    : [
        { key: "dashboard", label: "Home", path: "/dashboard" },
        { key: "budget", label: "Budget", path: "/budget" },
        { key: "documents", label: "Documents", path: "/documents" },
        { key: "share", label: "Share", path: "/share" },
      ];

  // Determine active tab based on current path
  const getActiveTab = (): string => {
    const currentPath = location.pathname;
    if (currentPath === "/budget" || currentPath === "/update-budget") {
      return "budget";
    }
    const matchedTab = headerNavTabs.find((tab) => currentPath === tab.path);
    return matchedTab?.key || "dashboard";
  };

  const activeTab = getActiveTab();

  // Profile dialog handlers
  const handleProfileOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
    setProfileDialogOpen(true);
  };

  const handleProfileClose = () => {
    setProfileDialogOpen(false);
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileClose();
    try {
      // If the user is in an Open Banking-invited ("OB-only") journey and logs out via the header/menu,
      // mark the invitation as rejected before ending the session (legacy parity).
      try {
        if (openBankingOnly) {
          await OnboardingService.rejectOpenBankInvitationIfPresent(user, {
            skipErrorCapture: true,
          });
        }
      } catch (inviteError) {
        // Non-blocking: proceed with logout even if this fails.
        // eslint-disable-next-line no-console
        console.warn('Failed to update Open Banking invitation status (rejected)', inviteError);
      }

      await logout({ source: "manual" });
    } finally {
      navigate("/auth", { replace: true });
    }
  };

  const handleLogoClick = () => {
    // For Open Banking-only users, the IEHub logo should not navigate anywhere.
    if (openBankingOnly) return;
    handleTabClick("/dashboard");
  };

  const handleTabClick = (path: string) => {
    // Open Banking-only lock: always stay on /open-banking
    if (openBankingOnly) {
      navigate("/open-banking");
      return;
    }
    const currentPath = location.pathname;

    // If user is on update-budget screen, ask that page to auto-save before leaving.
    // Navigation will be performed by the update-budget page after save succeeds.
    if (currentPath === "/update-budget") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("iehub:budget-before-leave", {
            detail: { targetPath: path },
          })
        );
      }
      return;
    }

    if (currentPath === "/documents") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("iehub:documents-before-leave", {
            detail: { targetPath: path },
          })
        );
      }
      return;
    }

    navigate(path);
  };

  const handleContactUsOpen = () => {
    // Open the in-app Contact Us dialog
    setContactUsDialogOpen(true);
  };

  const handleContactUsClose = () => {
    setContactUsDialogOpen(false);
  };

  // Measure header height and notify parent
  useEffect(() => {
    const updateHeights = () => {
      const newHeaderHeight = headerRef.current?.offsetHeight ?? 0;
      if (newHeaderHeight > 0 && onHeightChange) {
        onHeightChange(newHeaderHeight);
      }
    };

    const initialMeasurement = () => {
      requestAnimationFrame(() => {
        updateHeights();
        setTimeout(updateHeights, 100);
      });
    };

    initialMeasurement();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateHeights);
      if (headerRef.current) resizeObserver.observe(headerRef.current);
    }

    window.addEventListener("resize", updateHeights);
    return () => {
      window.removeEventListener("resize", updateHeights);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [onHeightChange]);

  // Hide header completely for Open Banking-only users and UW customers.
  // UW journey has its own chrome; the IEHub logo + profile avatar must not leak in.
  if (openBankingOnly || isUwCustomer) {
    return null;
  }

  return (
    <>
      {/* Mobile Header */}
      <Box
        ref={headerRef}
        sx={{
          display: { xs: "block", md: "none" },
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #e0e0e0",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 16px",
            height: "56px",
          }}
        >
          {/* Left: Logo and Hub */}
          <Box
            onClick={openBankingOnly ? undefined : handleLogoClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: openBankingOnly ? "default" : "pointer",
              ...(openBankingOnly
                ? {}
                : {
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }),
            }}
          >
            <Box
              component="img"
              src="/icons/logo.png"
              alt="IE Hub"
              sx={{
                width: 86,
                height: 44,
                borderRadius: 1,
              }}
            />
          </Box>

          {/* Right: User */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* User Avatar */}
            <IconButton
              size="small"
              onClick={handleProfileOpen}
              sx={{
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Avatar
                data-private
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: "14px",
                  bgcolor: "#033F44",
                }}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Desktop Header */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e0e0e0",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1.5,
            minHeight: "64px",
            maxWidth: "100%",
          }}
        >
          <Box sx={{
            display: "flex",
            gap: 5,
            alignItems: "center",
          }}>
            {/* Left: Logo and Hub */}
            <Box
              onClick={openBankingOnly ? undefined : handleLogoClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: openBankingOnly ? "default" : "pointer",
                ...(openBankingOnly
                  ? {}
                  : {
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }),
              }}
            >
              <Box
                component="img"
                src="/icons/logo.png"
                alt="IE Hub"
                sx={{
                  width: 86,
                  height: 44,
                  borderRadius: 1,
                }}
              />
            </Box>

            {/* Center: Navigation Tabs */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
              }}
            >
              {headerNavTabs.map((tab) => (
                <Typography
                  key={tab.key}
                  onClick={() => !tab.disabled && handleTabClick(tab.path)}
                  sx={{
                    cursor: tab.disabled ? "not-allowed" : "pointer",
                    opacity: tab.disabled ? 0.4 : 1,
                    fontSize: "16px",
                    fontWeight: activeTab === tab.key ? 600 : 400,
                    color: activeTab === tab.key ? "text.primary" : "#616161",
                    "&:hover": {
                      color: tab.disabled ? "#616161" : "text.primary",
                    },
                    transition: "color 0.2s",
                  }}
                >
                  {tab.label}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Right: User Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* User Menu */}
            <Box
              onClick={handleProfileOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Avatar
                data-private
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: "12px",
                  bgcolor: "#033F44",
                }}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
              <Typography
                data-private
                variant="body1"
                sx={{ fontSize: "14px", fontWeight: 400 }}
              >
                {userName}
              </Typography>
              <ArrowDropDownIcon sx={{ fontSize: "20px" }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Profile Dialog */}
      <ProfileDialog
        open={profileDialogOpen}
        onClose={handleProfileClose}
        userName={userName}
        userEmail={userEmail}
        onLogout={handleLogout}
        onContactUsClick={handleContactUsOpen}
        openBankingOnly={openBankingOnly}
        anchorEl={profileAnchorEl}
      />

      {/* Contact Us Dialog */}
      <ContactUsDialog
        open={contactUsDialogOpen}
        onClose={handleContactUsClose}
        {...(userEmail ? { userEmail } : {})}
      />
    </>
  );
};

export default Header;
